import cn from 'clsx'
import { Avatar } from '@/components/avatar/avatar'
import { Box } from '@/components/Box/Box'
import { Button, type Icon } from '@/components/Button/Button'
import { Text } from '@/components/Text/Text'

import css from './review-box.module.css'
import { Fragment } from 'react/jsx-runtime'

export type ReviewBoxCitation =
    | React.ReactElement<TwitterCitationProps>
    | React.ReactElement<AppReviewCitationProps>
    | React.ReactElement<GeneralCitationProps>

export type ReviewBoxProps = {
    citation: ReviewBoxCitation
    children: React.ReactNode
    className?: string
}

function ReviewBox({ citation, children, className }: ReviewBoxProps) {
    const isGeneralCitation = citation.type === ReviewBox.GeneralCitation

    return (
        <Box className={cn({ [css.general]: isGeneralCitation }, css.reviewBox, className)}>
            <Box className={css.citation}>{citation}</Box>
            <Box className={css.quote}>
                <Text size="body-sm" weight="medium">
                    {children}
                </Text>
            </Box>
        </Box>
    )
}

type TwitterCitationProps = {
    avatar: { src: string; alt?: string }
    label: string
    tweetLink: string
    icon: Icon
}

ReviewBox.TwitterCitation = function TwitterCitation({
    avatar,
    label,
    tweetLink,
    icon: TwitterIcon,
}: TwitterCitationProps) {
    return (
        <>
            <Box className={css.attributes}>
                <div>
                    <Avatar src={avatar.src} size={28} alt={avatar.alt} />
                </div>
                <Text size="minor-sm" width="medium">
                    {label}
                </Text>
            </Box>
            <Button startIcon={TwitterIcon} variant="quinary" href={tweetLink} target="_blank" />
        </>
    )
}

type AppReviewCitationProps = {
    label: string
    icon: Icon
    stars: 1 | 2 | 3 | 4 | 5
    starsAriaLabel: string
}

ReviewBox.AppReviewCitation = function AppReviewCitation({
    label,
    icon: PlatformIcon,
    stars,
    starsAriaLabel,
}: AppReviewCitationProps) {
    return (
        <>
            <Box className={css.attributes}>
                <div className={css.iconContainer}>
                    <PlatformIcon height={18} width={18} />
                </div>
                <Text size="minor-sm" weight="medium" width="medium">
                    {label}
                </Text>
            </Box>

            <Text size="minor-lg" weight="semibold" aria-description={starsAriaLabel}>
                <span className={css.stars} aria-hidden>
                    {Array.from({ length: stars }, (_, i) => i).map((i) => (
                        <Fragment key={i}>★</Fragment>
                    ))}
                </span>
            </Text>
        </>
    )
}

type GeneralCitationProps = {
    avatar?: { src: string; alt?: string }
    name: string
    title: string
}

ReviewBox.GeneralCitation = function GeneralCitation({
    avatar,
    name,
    title,
}: GeneralCitationProps) {
    return (
        <>
            {avatar && (
                <div>
                    <Avatar src={avatar.src} size={44} alt={avatar.alt} />
                </div>
            )}
            <Box className={cn(css.general, css.attributes)}>
                <Text size="minor-sm" width="medium">
                    {name}
                </Text>
                <Text size="body-xs" width="medium">
                    {title}
                </Text>
            </Box>
        </>
    )
}

export { ReviewBox }
